import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
// Using a simple tab implementation since Tabs might not exist
// We'll create a simple tab UI
import { BookOpen, FileText, Search } from 'lucide-react';
import { getHelpCenterUrl } from '@/lib/utils';
import type { Guide, Article, HelpCenterStructureNode } from '@/types';

interface ReferenceBlockSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: { id: string; type: 'guide' | 'article'; title: string; subtitle?: string; url?: string; helpCenterSlug?: string }) => void;
  helpCenterStructure?: HelpCenterStructureNode[]; // Help center structure to extract items from
  helpCenterSlug?: string; // Help center slug for URL construction
}

export const ReferenceBlockSelector: React.FC<ReferenceBlockSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  helpCenterStructure = [],
  helpCenterSlug,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'guides' | 'articles'>('guides');
  const [guides, setGuides] = useState<Array<Guide & { helpCenterSlug?: string }>>([]);
  const [articles, setArticles] = useState<Array<Article & { helpCenterSlug?: string }>>([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      const guidesList: Array<Guide & { helpCenterSlug?: string }> = [];
      const articlesList: Array<Article & { helpCenterSlug?: string }> = [];
      
      if (helpCenterStructure.length > 0) {
        extractItemsFromStructure(helpCenterStructure, helpCenterSlug || '', guidesList, articlesList);
      }
      
      setGuides(guidesList);
      setArticles(articlesList);
      setSearchQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, helpCenterStructure, helpCenterSlug]);

  // Recursive function to extract items from structure nodes
  const extractItemsFromStructure = (
    nodes: HelpCenterStructureNode[],
    helpCenterSlug: string,
    guides: Array<Guide & { helpCenterSlug?: string }>,
    articles: Array<Article & { helpCenterSlug?: string }>
  ) => {
    for (const node of nodes) {
      if (node.type === 'item' && node.item) {
        if (node.item.item_type === 'guide' && node.item.guide) {
          const guide = node.item.guide;
          // Ensure ID is a string
          guides.push({
            ...guide,
            id: String(node.item.item_id),
            helpCenterSlug: helpCenterSlug,
          });
        } else if (node.item.item_type === 'article' && node.item.article) {
          const article = node.item.article;
          articles.push({
            ...article,
            id: String(node.item.item_id),
            helpCenterSlug: helpCenterSlug,
          });
        }
      }
      // Recursively process children
      if (node.children && node.children.length > 0) {
        extractItemsFromStructure(node.children, helpCenterSlug, guides, articles);
      }
    }
  };


  const filteredGuides = guides.filter(
    (guide) =>
      guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (guide.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredArticles = articles.filter(
    (article) =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (article.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectGuide = (guide: Guide & { helpCenterSlug?: string }) => {
    // Build URL for guide in help center
    let url = guide.publicUrl;
    if (guide.helpCenterSlug && !url) {
      // If guide is in help center but no publicUrl, construct help center URL
      // The actual path would need to be constructed from the help center structure
      // For now, use the publicUrl if available
      url = guide.publicUrl;
    }

    onSelect({
      id: guide.id,
      type: 'guide',
      title: guide.title,
      subtitle: guide.description || undefined,
      url: url || undefined,
      helpCenterSlug: guide.helpCenterSlug,
    });
    onClose();
  };

  const handleSelectArticle = (article: Article & { helpCenterSlug?: string }) => {
    // Build URL for article in help center
    let url: string | undefined;
    if (article.helpCenterSlug) {
      url = getHelpCenterUrl(article.helpCenterSlug, `/${article.slug}`);
    }

    onSelect({
      id: article.id,
      type: 'article',
      title: article.title,
      subtitle: article.description || undefined,
      url: url,
      helpCenterSlug: article.helpCenterSlug,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('editor.referenceBlockSelector.insertReference')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('editor.referenceBlockSelector.searchPlaceholder')}
              className="pl-9"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setActiveTab('guides')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'guides'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <BookOpen className="h-4 w-4 inline mr-2" />
              {t('editor.referenceBlockSelector.guides')}
            </button>
            <button
              onClick={() => setActiveTab('articles')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'articles'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <FileText className="h-4 w-4 inline mr-2" />
              {t('editor.referenceBlockSelector.articles')}
            </button>
          </div>

          {/* Guides Tab */}
          {activeTab === 'guides' && (
            <div className="flex-1 overflow-y-auto border rounded-md mt-4">
              {filteredGuides.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery ? t('editor.referenceBlockSelector.noGuidesFound') : t('editor.referenceBlockSelector.noGuidesAvailable')}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredGuides.map((guide) => (
                    <div
                      key={guide.id}
                      onClick={() => handleSelectGuide(guide)}
                      className="w-full p-4 hover:bg-accent transition-colors cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <BookOpen className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{guide.title}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Articles Tab */}
          {activeTab === 'articles' && (
            <div className="flex-1 overflow-y-auto border rounded-md mt-4">
              {filteredArticles.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery ? t('editor.referenceBlockSelector.noArticlesFound') : t('editor.referenceBlockSelector.noArticlesAvailable')}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredArticles.map((article) => (
                    <div
                      key={article.id}
                      onClick={() => handleSelectArticle(article)}
                      className="w-full p-4 hover:bg-accent transition-colors cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{article.title}</div>
                        </div>
                      </div>
                    </div>
              ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
